import React, { useState } from 'react';
import { X, Plus, Minus, Printer, Send, Receipt } from 'lucide-react';
import { Order, Booking } from '../types';
import { format } from 'date-fns';
import { cn } from '../utils/cn';
import jsPDF from 'jspdf';

interface InvoiceGenerationModalProps {
  orders: Order[];
  activeBooking?: Booking;
  onClose: () => void;
}

interface ServiceItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

const InvoiceGenerationModal: React.FC<InvoiceGenerationModalProps> = ({
  orders,
  activeBooking,
  onClose,
}) => {
  const [additionalServices, setAdditionalServices] = useState<ServiceItem[]>([
    { id: 'spa', name: 'Spa & Wellness Package', price: 2500, quantity: 0 },
    { id: 'laundry', name: 'Laundry Service', price: 500, quantity: 0 },
    { id: 'airport', name: 'Airport Transfer', price: 1500, quantity: 0 },
    { id: 'late-checkout', name: 'Late Checkout (2 PM)', price: 800, quantity: 0 },
    { id: 'extra-bed', name: 'Extra Bed', price: 1200, quantity: 0 },
    { id: 'mini-bar', name: 'Mini Bar Service', price: 300, quantity: 0 },
  ]);

  const updateServiceQuantity = (id: string, change: number) => {
    setAdditionalServices(prev => 
      prev.map(service => 
        service.id === id 
          ? { ...service, quantity: Math.max(0, service.quantity + change) }
          : service
      )
    );
  };

  const calculateFoodTotal = () => {
    return orders.reduce((total, order) => total + parseFloat(order.totalAmount), 0);
  };

  const calculateServicesTotal = () => {
    return additionalServices.reduce((total, service) => 
      total + (service.price * service.quantity), 0
    );
  };

  const calculateGrandTotal = () => {
    return calculateFoodTotal() + calculateServicesTotal();
  };

  const generateInvoicePDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('HOTEL INVOICE', 105, 20, { align: 'center' });
    
    // Hotel info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Hotel Continental', 105, 30, { align: 'center' });
    doc.text('Powered by Snowtable', 105, 35, { align: 'center' });
    
    // Invoice details
    doc.setFontSize(12);
    doc.text(`Invoice #: ${activeBooking?.bookingId || 'N/A'}`, 20, 50);
    doc.text(`Date: ${format(new Date(), 'MMM dd, yyyy')}`, 20, 60);
    doc.text(`Room: ${activeBooking?.roomId || 'N/A'}`, 20, 70);
    
    // Guest info
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, 90);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(activeBooking?.guestName || 'Guest', 20, 100);
    if (activeBooking?.phone) {
      doc.text(activeBooking.phone, 20, 110);
    }
    if (activeBooking?.email) {
      doc.text(activeBooking.email, 20, 120);
    }
    
    let yPosition = 140;
    
    // Food Orders Section
    if (orders.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Food Orders:', 20, yPosition);
      yPosition += 10;
      
      // Table header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Item', 20, yPosition);
      doc.text('Qty', 120, yPosition);
      doc.text('Price', 140, yPosition);
      doc.text('Total', 170, yPosition);
      
      // Table line
      doc.line(20, yPosition + 5, 190, yPosition + 5);
      yPosition += 15;
      
      orders.forEach((order) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(order.itemName, 20, yPosition);
        doc.text(order.quantity.toString(), 120, yPosition);
        doc.text(`₹${order.unitPrice}`, 140, yPosition);
        doc.text(`₹${order.totalAmount}`, 170, yPosition);
        yPosition += 10;
      });
      
      yPosition += 10;
    }
    
    // Additional Services Section
    const servicesWithQuantity = additionalServices.filter(s => s.quantity > 0);
    if (servicesWithQuantity.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Additional Services:', 20, yPosition);
      yPosition += 10;
      
      // Table header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Service', 20, yPosition);
      doc.text('Qty', 120, yPosition);
      doc.text('Price', 140, yPosition);
      doc.text('Total', 170, yPosition);
      
      // Table line
      doc.line(20, yPosition + 5, 190, yPosition + 5);
      yPosition += 15;
      
      servicesWithQuantity.forEach((service) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(service.name, 20, yPosition);
        doc.text(service.quantity.toString(), 120, yPosition);
        doc.text(`₹${service.price}`, 140, yPosition);
        doc.text(`₹${service.price * service.quantity}`, 170, yPosition);
        yPosition += 10;
      });
      
      yPosition += 10;
    }
    
    // Totals
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Food Orders Total: ₹${calculateFoodTotal().toFixed(2)}`, 120, yPosition);
    yPosition += 10;
    
    if (calculateServicesTotal() > 0) {
      doc.text(`Services Total: ₹${calculateServicesTotal().toFixed(2)}`, 120, yPosition);
      yPosition += 10;
    }
    
    doc.setFontSize(14);
    doc.text(`GRAND TOTAL: ₹${calculateGrandTotal().toFixed(2)}`, 120, yPosition);
    
    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Generated at: ' + new Date().toLocaleString('en-IN'), 105, doc.internal.pageSize.height - 20, { align: 'center' });
    
    // Open PDF in new tab
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  const sendInvoice = () => {
    // TODO: Implement email sending logic
    alert('Invoice will be sent to customer email');
  };

  return (
    <>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          margin: 0, 
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          zIndex: 9999
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" 
          style={{ 
            margin: 0,
            backgroundColor: 'white',
            borderRadius: '1rem'
          }}
        >
          {/* Header */}
          <div className="bg-primary-500 text-white p-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Generate Invoice</h2>
                <p className="text-primary-100">Room {activeBooking?.roomId} - {activeBooking?.guestName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Food Orders */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800">Food Orders</h3>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    No food orders found
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order) => (
                      <div key={order.orderId} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{order.itemName}</span>
                          <span className="text-sm text-gray-600">Qty: {order.quantity}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Unit: ₹{order.unitPrice}</span>
                          <span className="font-bold text-green-600">₹{order.totalAmount}</span>
                        </div>
                      </div>
                    ))}
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="flex justify-between font-bold">
                        <span>Food Total:</span>
                        <span className="text-primary-600">₹{calculateFoodTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Additional Services */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800">Additional Services</h3>
                <div className="space-y-3">
                  {additionalServices.map((service) => (
                    <div key={service.id} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{service.name}</span>
                        <span className="text-sm text-gray-600">₹{service.price}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateServiceQuantity(service.id, -1)}
                            className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center">{service.quantity}</span>
                          <button
                            onClick={() => updateServiceQuantity(service.id, 1)}
                            className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="font-bold text-primary-600">
                          ₹{(service.price * service.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="flex justify-between font-bold">
                      <span>Services Total:</span>
                      <span className="text-primary-600">₹{calculateServicesTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Grand Total */}
            <div className="mt-6 bg-primary-50 p-4 rounded-lg border-2 border-primary-200">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-primary-800">Grand Total:</span>
                <span className="text-2xl font-bold text-primary-600">₹{calculateGrandTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Footer with Action Buttons */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Invoice will include all selected items and services
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={generateInvoicePDF}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print Invoice</span>
                </button>
                
                <button
                  onClick={sendInvoice}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Invoice</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceGenerationModal;
